import React from 'react';
import { Modal } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { ClientContext, ServicesContext } from '~/services';

export interface PatientPrintOptionsType {
    riskOpen: boolean;
    riskClosed: boolean;
    qualityOpen: boolean;
    qualityClosed: boolean;
    focOpen: boolean;
    focClosed: boolean;
    hxLabs: boolean;
    hxMedications: boolean;
    hxSpecialtyVisits: boolean;
    hxDiagnoses: boolean;
    hxVitals: boolean;
    hxInptOutpt: boolean;
}
export const defaultPatientPrintOptions: PatientPrintOptionsType = {
    riskOpen: false,
    riskClosed: false,
    qualityOpen: false,
    qualityClosed: false,
    focOpen: false,
    focClosed: false,
    hxLabs: false,
    hxMedications: false,
    hxSpecialtyVisits: false,
    hxDiagnoses: false,
    hxVitals: false,
    hxInptOutpt: false,
};
interface PatientPrintSummaryProps {
    patientId: number;
}
export function PatientPrintSummary({ patientId }: PatientPrintSummaryProps) {
    const clientContext = React.useContext(ClientContext);
    const context = React.useContext(ServicesContext);
    const { savedPatientPrintOptions } = context.user.getSavedSearchData();
    const [show, setShow] = React.useState(false);
    const initOptions = savedPatientPrintOptions || defaultPatientPrintOptions;
    const [printOptions, setPrintOptions] = React.useState<PatientPrintOptionsType>(initOptions);
    type PrintOption = keyof typeof printOptions;
    const allSelected = Object.entries(printOptions).every(([key, value]) => {
        // ignore foc options if disabled by toggle
        if (
            context.user.hasFeatureToggle('hidden_foc') &&
            (key === 'focOpen' || key === 'focClosed')
        )
            return true;
        return value;
    });
    const allRiskSelected = printOptions.riskOpen && printOptions.riskClosed;
    const allFocSelected = printOptions.focOpen && printOptions.focClosed;
    const allQualitySelected = printOptions.qualityOpen && printOptions.qualityClosed;
    let allHxSelected =
        printOptions.hxLabs &&
        printOptions.hxMedications &&
        printOptions.hxSpecialtyVisits &&
        printOptions.hxDiagnoses &&
        printOptions.hxVitals;
    if (context.user.hasFeatureToggle('inpt_outpt'))
        allHxSelected = allHxSelected && printOptions.hxInptOutpt;
    const someSelected = Object.values(printOptions).some((option) => option);

    function handleOptionSelect(e: React.ChangeEvent<HTMLInputElement>) {
        const { checked, name } = e.target;
        setPrintOptions((prevOptions) => {
            const updatedOptions = { ...prevOptions };
            if (name === 'selectAll') {
                Object.keys(updatedOptions).forEach((key: PrintOption) => {
                    updatedOptions[key] = !allSelected;
                });
            } else if (name === 'riskAll') {
                updatedOptions.riskOpen = !allRiskSelected;
                updatedOptions.riskClosed = !allRiskSelected;
            } else if (name === 'focAll') {
                updatedOptions.focOpen = !allFocSelected;
                updatedOptions.focClosed = !allFocSelected;
            } else if (name === 'qualityAll') {
                updatedOptions.qualityOpen = !allQualitySelected;
                updatedOptions.qualityClosed = !allQualitySelected;
            } else if (name === 'hxAll') {
                updatedOptions.hxLabs = !allHxSelected;
                updatedOptions.hxMedications = !allHxSelected;
                updatedOptions.hxSpecialtyVisits = !allHxSelected;
                updatedOptions.hxDiagnoses = !allHxSelected;
                updatedOptions.hxVitals = !allHxSelected;
                if (context.user.hasFeatureToggle('inpt_outpt')) {
                    updatedOptions.hxInptOutpt = !allHxSelected;
                }
            } else {
                updatedOptions[name as PrintOption] = checked;
            }
            return updatedOptions;
        });
    }

    function handleClose() {
        setPrintOptions(initOptions);
        setShow(false);
    }

    async function handleSubmit() {
        const selectedOptions: { [key: string]: string } = {};
        Object.keys(printOptions).forEach((key: PrintOption) => {
            if (!context.user.hasFeatureToggle('inpt_outpt') && key === 'hxInptOutpt') return;
            if (
                context.user.hasFeatureToggle('hidden_foc') &&
                (key === 'focOpen' || key === 'focClosed')
            )
                return;
            if (printOptions[key]) selectedOptions[key] = '1';
        });
        const params = new URLSearchParams(selectedOptions);
        const pdfWindow = window.open(
            `/api/patients/${patientId}/${clientContext.reportYear}/pdf?${params}`
        );
        if (pdfWindow) {
            pdfWindow.onload = () => {
                if (pdfWindow.document.title !== 'PDF Error') {
                    pdfWindow.print();
                }
            };
        }
        context.user.setSavedSearchData('patient_print_options', printOptions);
        setShow(false);
    }

    return (
        <>
            <button
                id="patient-print-button"
                className="link-button"
                type="button"
                onClick={() => setShow(true)}
            >
                Print
            </button>
            <BSModal
                id="patient-print-modal"
                show={show}
                onHide={handleClose}
                aria-labelledby="patient-print-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="patient-print-title" role="heading" aria-level={1}>
                        Patient Print Summary
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Please select which information you would like to include in the patient
                        summary.
                    </p>
                    <div>
                        <label className="form-label">
                            <input
                                type="checkbox"
                                name="selectAll"
                                checked={allSelected}
                                onChange={handleOptionSelect}
                            />
                            <span className="fw-bold">Select All</span>
                        </label>
                        <div>
                            <div className="fw-bold">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="riskAll"
                                        checked={allRiskSelected}
                                        onChange={handleOptionSelect}
                                    />
                                    Attestation
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="riskOpen"
                                        checked={printOptions.riskOpen}
                                        onChange={handleOptionSelect}
                                    />
                                    Open
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="riskClosed"
                                        checked={printOptions.riskClosed}
                                        onChange={handleOptionSelect}
                                    />
                                    Closed
                                </label>
                            </div>
                        </div>
                        {!context.user.hasFeatureToggle('hidden_foc') && (
                            <div>
                                <div className="fw-bold">
                                    <label className="form-label">
                                        <input
                                            type="checkbox"
                                            name="focAll"
                                            checked={allFocSelected}
                                            onChange={handleOptionSelect}
                                        />
                                        AgeWise
                                    </label>
                                </div>
                                <div className="ms-3">
                                    <label className="form-label">
                                        <input
                                            type="checkbox"
                                            name="focOpen"
                                            checked={printOptions.focOpen}
                                            onChange={handleOptionSelect}
                                        />
                                        Open
                                    </label>
                                </div>
                                <div className="ms-3">
                                    <label className="form-label">
                                        <input
                                            type="checkbox"
                                            name="focClosed"
                                            checked={printOptions.focClosed}
                                            onChange={handleOptionSelect}
                                        />
                                        Closed
                                    </label>
                                </div>
                            </div>
                        )}
                        <div>
                            <div className="fw-bold">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="qualityAll"
                                        checked={allQualitySelected}
                                        onChange={handleOptionSelect}
                                    />
                                    Quality
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="qualityOpen"
                                        checked={printOptions.qualityOpen}
                                        onChange={handleOptionSelect}
                                    />
                                    Open
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="qualityClosed"
                                        checked={printOptions.qualityClosed}
                                        onChange={handleOptionSelect}
                                    />
                                    Closed
                                </label>
                            </div>
                        </div>
                        <div>
                            <div className="fw-bold">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="hxAll"
                                        checked={allHxSelected}
                                        onChange={handleOptionSelect}
                                    />
                                    Health History
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="hxLabs"
                                        checked={printOptions.hxLabs}
                                        onChange={handleOptionSelect}
                                    />
                                    Labs
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="hxMedications"
                                        checked={printOptions.hxMedications}
                                        onChange={handleOptionSelect}
                                    />
                                    Medications
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="hxSpecialtyVisits"
                                        checked={printOptions.hxSpecialtyVisits}
                                        onChange={handleOptionSelect}
                                    />
                                    Specialty Visits
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="hxDiagnoses"
                                        checked={printOptions.hxDiagnoses}
                                        onChange={handleOptionSelect}
                                    />
                                    Diagnoses
                                </label>
                            </div>
                            <div className="ms-3">
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        name="hxVitals"
                                        checked={printOptions.hxVitals}
                                        onChange={handleOptionSelect}
                                    />
                                    Vitals
                                </label>
                            </div>
                            {context.user.hasFeatureToggle('inpt_outpt') && (
                                <div className="ms-3">
                                    <label className="form-label">
                                        <input
                                            type="checkbox"
                                            name="hxInptOutpt"
                                            checked={printOptions.hxInptOutpt}
                                            onChange={handleOptionSelect}
                                        />
                                        Inpatient / Outpatient
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={handleClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!someSelected}
                        onClick={handleSubmit}
                    >
                        Print
                    </button>
                </Modal.Footer>
            </BSModal>
        </>
    );
}
